import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Palette,
  PartyPopper,
  Phone,
  Rocket,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import {
  LAUNCH_STEPS,
  useInitLaunchProgress,
  useOrgLaunchProgress,
  useUpdateLaunchProgress,
} from "@/hooks/useOrgLaunchProgress";
import { supabase } from "@/integrations/supabase/client";
import RoleOrientationCards from "@/components/onboarding/RoleOrientationCards";

/* ─── Step 1: Branding ─── */

const StepBranding = ({
  orgData,
  setOrgData,
  errors,
}: {
  orgData: any;
  setOrgData: (d: any) => void;
  errors: Record<string, string>;
}) => {
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const path = `logos/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("org-assets").upload(path, file);
    if (error) {
      toast.error("Upload failed");
      return;
    }
    const {
      data: { publicUrl },
    } = supabase.storage.from("org-assets").getPublicUrl(path);
    setOrgData({ ...orgData, logo_url: publicUrl });
    toast.success("Logo uploaded");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-foreground">Brand Your Organization</h2>
        <p className="text-sm text-muted-foreground mt-1">Set up your identity — name, logo, and color</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">
            Organization Name <span className="text-destructive">*</span>
          </label>
          <Input
            value={orgData.name || ""}
            onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
            placeholder="Your Organization"
            className={errors.name ? "border-destructive" : ""}
          />
          {errors.name && <p className="text-[11px] text-destructive mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">Logo</label>
          <div className="flex items-center gap-4">
            {orgData.logo_url ? (
              <img
                src={orgData.logo_url}
                alt="Logo"
                className="w-16 h-16 rounded-xl object-contain border border-border"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center">
                <Upload className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              <span className="text-sm text-primary hover:underline">
                {orgData.logo_url ? "Change logo" : "Upload logo"}
              </span>
            </label>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">Primary Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={orgData.primary_color || "#6c5ce7"}
              onChange={(e) => setOrgData({ ...orgData, primary_color: e.target.value })}
              className="w-10 h-10 rounded-lg cursor-pointer border-0"
            />
            <Input
              value={orgData.primary_color || "#6c5ce7"}
              onChange={(e) => setOrgData({ ...orgData, primary_color: e.target.value })}
              className="font-mono text-sm w-32"
            />
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="p-4 rounded-xl border border-border bg-muted/30">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Preview</p>
        <div className="flex items-center gap-3">
          {orgData.logo_url ? (
            <img src={orgData.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: orgData.primary_color || "#6c5ce7" }} />
          )}
          <span className="font-semibold text-foreground">{orgData.name || "Your Organization"}</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Step 2: Basics ─── */

const StepBasics = ({ orgData, setOrgData }: { orgData: any; setOrgData: (d: any) => void }) => {
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-foreground">Set Your Basics</h2>
        <p className="text-sm text-muted-foreground mt-1">Contact information and essential details</p>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">
              <Phone className="w-3 h-3 inline mr-1" />
              Phone
            </label>
            <Input
              value={orgData.phone || ""}
              onChange={(e) => setOrgData({ ...orgData, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">
              <Mail className="w-3 h-3 inline mr-1" />
              Email
            </label>
            <Input
              value={orgData.email || ""}
              onChange={(e) => setOrgData({ ...orgData, email: e.target.value })}
              placeholder="info@org.com"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">
            <Building2 className="w-3 h-3 inline mr-1" />
            Address
          </label>
          <Input
            value={orgData.address || ""}
            onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
            placeholder="123 Main St, City, State"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">
            <Globe className="w-3 h-3 inline mr-1" />
            Website
          </label>
          <Input
            value={orgData.website_url || ""}
            onChange={(e) => setOrgData({ ...orgData, website_url: e.target.value })}
            placeholder="https://yourorg.com"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">
            <Clock className="w-3 h-3 inline mr-1" />
            Timezone
          </label>
          <Input
            value={orgData.timezone || detectedTimezone}
            onChange={(e) => setOrgData({ ...orgData, timezone: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">Welcome Message</label>
          <Textarea
            value={orgData.welcome_message || ""}
            onChange={(e) => setOrgData({ ...orgData, welcome_message: e.target.value })}
            placeholder="A short welcome message for your members..."
            className="min-h-[60px] text-sm"
          />
        </div>
      </div>
    </div>
  );
};

/* ─── Step 3: Website ─── */

const StepWebsite = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-lg font-bold text-foreground">Launch Your Website</h2>
      <p className="text-sm text-muted-foreground mt-1">Go live instantly with your public site</p>
    </div>
    <div className="grid gap-4">
      <div className="p-5 rounded-xl border border-primary/20 bg-primary/5 space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" />
          <span className="font-semibold text-foreground">Quick Start Template</span>
        </div>
        <p className="text-sm text-muted-foreground">
          We'll create basic pages (Home, Events, Membership) that you can customize later from the Website Builder.
        </p>
      </div>
    </div>
    <p className="text-[11px] text-muted-foreground">
      You can always customize your website later from the Website Builder in the sidebar.
    </p>
  </div>
);

/* ─── Completion Screen with Role Orientation ─── */

const CompletionScreen = ({ onFinish }: { onFinish: (path?: string) => void }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="space-y-8 py-4">
    <div className="text-center space-y-3">
      <div className="w-20 h-20 mx-auto rounded-full bg-success/10 flex items-center justify-center">
        <PartyPopper className="w-10 h-10 text-success" />
      </div>
      <h1 className="text-2xl font-bold text-foreground">You're Live! 🎉</h1>
      <p className="text-sm text-muted-foreground">Your organization is set up and ready to go.</p>
    </div>

    {/* Role-specific orientation */}
    <RoleOrientationCards onNavigate={(path) => onFinish(path)} />

    <div className="text-center pt-2">
      <Button onClick={() => onFinish("/")} variant="outline" size="sm" className="gap-1">
        Go to Dashboard <ArrowRight className="w-3.5 h-3.5" />
      </Button>
    </div>
  </motion.div>
);

/* ─── Main Onboarding Flow ─── */

const OrgOnboardingFlow = ({ onComplete }: { onComplete: () => void }) => {
  const { orgId } = useOrg();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: progress } = useOrgLaunchProgress();
  const initProgress = useInitLaunchProgress();
  const updateProgress = useUpdateLaunchProgress();

  // Trial users skip the landing screen and go straight into setup
  const isTrialUser = user?.user_metadata?.signup_source === "free_trial";

  const [started, setStarted] = useState(isTrialUser);
  const [currentStep, setCurrentStep] = useState(1);
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgData, setOrgData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load existing org data
  const { data: org } = useQuery({
    queryKey: ["org_for_onboarding", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase.from("organizations").select("*").eq("id", orgId).single();
      return data;
    },
  });

  // Initialize org data when loaded
  useEffect(() => {
    if (org && !orgData._loaded) {
      setOrgData({
        _loaded: true,
        name: org.name,
        logo_url: org.logo_url,
        primary_color: org.primary_color || "#6c5ce7",
        timezone: org.timezone,
        phone: (org as unknown as Record<string, string>).phone || "",
        email: (org as unknown as Record<string, string>).contact_email || "",
        address: (org as unknown as Record<string, string>).address || "",
        website_url: (org as unknown as Record<string, string>).website_url || "",
        welcome_message: (org as unknown as Record<string, string>).welcome_message || "",
      });
    }
  }, [org, orgData._loaded]);

  // Resume from saved progress
  useEffect(() => {
    if (progress && !started && progress.current_step > 1 && !progress.is_complete) {
      setCurrentStep(progress.current_step);
      setStarted(true);
    }
    if (progress?.is_complete && !completed) {
      setCompleted(true);
    }
  }, [progress, started, completed]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (currentStep === 1 && !orgData.name?.trim()) {
      e.name = "Organization name is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Auto-init for trial users who skip the landing screen
  useEffect(() => {
    if (isTrialUser && !progress && orgId) {
      initProgress.mutate();
    }
  }, [isTrialUser, progress, orgId]);

  const handleStart = async () => {
    if (!progress) await initProgress.mutateAsync();
    setStarted(true);
  };

  const saveStepData = async () => {
    setSaving(true);
    try {
      if (currentStep === 1) {
        const updates: any = {};
        if (orgData.name) updates.name = orgData.name;
        if (orgData.logo_url) updates.logo_url = orgData.logo_url;
        if (orgData.primary_color) updates.primary_color = orgData.primary_color;
        if (Object.keys(updates).length > 0) {
          await supabase.from("organizations").update(updates).eq("id", orgId);
        }
        // Sync branding to website_config
        if (orgData.primary_color || orgData.logo_url || orgData.name) {
          const wcUpdates: any = {};
          if (orgData.primary_color) wcUpdates.primary_color = orgData.primary_color;
          if (orgData.logo_url) wcUpdates.logo_url = orgData.logo_url;
          if (orgData.name) wcUpdates.site_title = orgData.name;
          await supabase.from("website_config").update(wcUpdates).eq("org_id", orgId);
        }
      }
      if (currentStep === 2) {
        const updates: any = {};
        if (orgData.timezone) updates.timezone = orgData.timezone;
        if (orgData.phone) updates.phone = orgData.phone;
        if (orgData.address) updates.address = orgData.address;
        if (orgData.website_url) updates.website_url = orgData.website_url;
        if (orgData.welcome_message) updates.welcome_message = orgData.welcome_message;
        if (Object.keys(updates).length > 0) {
          await supabase.from("organizations").update(updates).eq("id", orgId);
        }
      }
    } catch (e: any) {
      console.error("Save error:", e);
    }
    setSaving(false);
  };

  const goNext = async () => {
    if (!validate()) return;
    await saveStepData();

    const newCompleted = [...(progress?.completed_steps || [])];
    if (!newCompleted.includes(currentStep)) newCompleted.push(currentStep);
    const pct = Math.round((newCompleted.length / LAUNCH_STEPS.length) * 100);

    if (currentStep >= LAUNCH_STEPS.length) {
      if (progress?.id) {
        await updateProgress.mutateAsync({
          id: progress.id,
          completed_steps: newCompleted,
          progress_percent: 100,
          is_complete: true,
          completed_at: new Date().toISOString(),
          current_step: currentStep,
        });
      }
      setCompleted(true);
    } else {
      const next = currentStep + 1;
      if (progress?.id) {
        await updateProgress.mutateAsync({
          id: progress.id,
          completed_steps: newCompleted,
          progress_percent: pct,
          current_step: next,
        });
      }
      setCurrentStep(next);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setErrors({});
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = (path?: string) => {
    onComplete();
    if (path && path !== "/") {
      navigate(path);
    }
  };

  // Landing
  if (!started) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center space-y-8"
        >
          <div className="space-y-3">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
              <Rocket className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Launch Your Organization</h1>
            <p className="text-sm text-muted-foreground">Get fully set up and live in just 3 quick steps</p>
          </div>

          {/* Step preview */}
          <div className="space-y-2 text-left">
            {LAUNCH_STEPS.map((s, i) => {
              const icons = [Palette, MapPin, Globe];
              const StepIcon = icons[i];
              return (
                <div key={s.step} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <StepIcon className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-sm text-foreground">{s.title}</span>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            <Progress value={0} className="h-2" />
            <p className="text-xs text-muted-foreground">Estimated time: ~3 minutes</p>
          </div>
          <Button size="lg" onClick={handleStart} className="gap-2 px-8">
            <Rocket className="w-5 h-5" /> Get Started
          </Button>
        </motion.div>
      </div>
    );
  }

  // Completion
  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <CompletionScreen onFinish={handleFinish} />
        </div>
      </div>
    );
  }

  // Steps
  const stepIcons = [Palette, MapPin, Globe];
  void stepIcons;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        {/* Progress */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Step {currentStep} of {LAUNCH_STEPS.length}
            </span>
            <span>{Math.round(((currentStep - 1) / LAUNCH_STEPS.length) * 100)}%</span>
          </div>
          <Progress value={((currentStep - 1) / LAUNCH_STEPS.length) * 100} className="h-2" />
          <div className="flex gap-2">
            {LAUNCH_STEPS.map((s) => (
              <div
                key={s.step}
                className={`flex-1 h-1 rounded-full transition-colors ${s.step <= currentStep ? "bg-primary" : "bg-muted"}`}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 rounded-2xl border border-border bg-card min-h-[400px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {currentStep === 1 && <StepBranding orgData={orgData} setOrgData={setOrgData} errors={errors} />}
              {currentStep === 2 && <StepBasics orgData={orgData} setOrgData={setOrgData} />}
              {currentStep === 3 && <StepWebsite />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={goBack} disabled={currentStep === 1} className="gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex gap-2">
            {currentStep < LAUNCH_STEPS.length && (
              <Button
                variant="ghost"
                onClick={() => {
                  setErrors({});
                  setCurrentStep(currentStep + 1);
                }}
                className="text-muted-foreground text-sm"
              >
                Skip
              </Button>
            )}
            <Button onClick={goNext} disabled={saving} className="gap-1 min-w-[120px]">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : currentStep >= LAUNCH_STEPS.length ? (
                <>
                  Complete <Check className="w-4 h-4" />
                </>
              ) : (
                <>
                  Continue <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgOnboardingFlow;
